import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Plus, Edit2, Trash2, FileText, Tag, Folder, User as UserIcon, Save,
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import AdminCard from '../../components/admin/AdminCard';
import AdminTable from '../../components/admin/AdminTable';
import AdminModal from '../../components/admin/AdminModal';
import { blogAdminAPI } from '../../services/api';
import { useProgram } from '../../contexts/ProgramContext';
import '../../styles/admin-shared.css';

const STATUS_LABELS = {
  DRAFT:     { label: 'Rascunho',  color: '#9aa0a6' },
  SCHEDULED: { label: 'Agendado',  color: '#f4b400' },
  PUBLISHED: { label: 'Publicado', color: '#34a853' },
  ARCHIVED:  { label: 'Arquivado', color: '#5f6368' },
};

const TABS = [
  { id: 'posts',      label: 'Posts',      icon: <FileText size={16} /> },
  { id: 'categories', label: 'Categorias', icon: <Folder   size={16} /> },
  { id: 'tags',       label: 'Tags',       icon: <Tag      size={16} /> },
  { id: 'authors',    label: 'Autores',    icon: <UserIcon size={16} /> },
];

const BlogListPage = () => {
  const navigate = useNavigate();
  const { slug } = useProgram();

  const [tab, setTab] = useState('posts');
  const [loading, setLoading] = useState(true);

  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [tags, setTags] = useState([]);
  const [authors, setAuthors] = useState([]);

  // Modal state for simple CRUD (categories, tags, authors)
  const [modal, setModal] = useState({ open: false, kind: null, item: null });
  const [form, setForm] = useState({});

  const fetchAll = async () => {
    setLoading(true);
    try {
      const [p, c, t, a] = await Promise.all([
        blogAdminAPI.listPosts().catch(() => ({ data: { data: [] } })),
        blogAdminAPI.listCategories().catch(() => ({ data: { data: [] } })),
        blogAdminAPI.listTags().catch(() => ({ data: { data: [] } })),
        blogAdminAPI.listAuthors().catch(() => ({ data: { data: [] } })),
      ]);
      setPosts(p.data.data || []);
      setCategories(c.data.data || []);
      setTags(t.data.data || []);
      setAuthors(a.data.data || []);
    } catch (e) {
      console.error('Blog admin: fetch error', e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, [slug]);

  // -- POST actions ---------------------------------------------------------
  const handleDeletePost = async (post) => {
    if (!window.confirm(`Excluir post "${post.title}"?`)) return;
    await blogAdminAPI.deletePost(post.id);
    fetchAll();
  };

  // -- Generic CRUD modal ---------------------------------------------------
  const openModal = (kind, item = null) => {
    let defaults = {};
    if (kind === 'category') defaults = { name: '', slug: '', description: '', sort_order: 0, is_active: true, program_id: '' };
    if (kind === 'tag')      defaults = { name: '', slug: '' };
    if (kind === 'author')   defaults = { name: '', slug: '', bio: '', avatar_url: '', email: '', twitter: '', linkedin: '', is_active: true };
    setForm(item ? { ...defaults, ...item, program_id: item.program_id || '' } : defaults);
    setModal({ open: true, kind, item });
  };
  const closeModal = () => setModal({ open: false, kind: null, item: null });

  const handleModalSave = async (e) => {
    e.preventDefault();
    const { kind, item } = modal;
    try {
      if (kind === 'category') {
        const payload = { ...form, program_id: form.program_id || null };
        if (item) await blogAdminAPI.updateCategory(item.id, payload);
        else      await blogAdminAPI.createCategory(payload);
      } else if (kind === 'tag') {
        if (item) await blogAdminAPI.updateTag(item.id, form);
        else      await blogAdminAPI.createTag(form);
      } else if (kind === 'author') {
        if (item) await blogAdminAPI.updateAuthor(item.id, form);
        else      await blogAdminAPI.createAuthor(form);
      }
      closeModal();
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Erro ao salvar');
    }
  };

  const handleDeleteGeneric = async (kind, item) => {
    if (!window.confirm(`Excluir "${item.name}"?`)) return;
    try {
      if (kind === 'category') await blogAdminAPI.deleteCategory(item.id);
      if (kind === 'tag')      await blogAdminAPI.deleteTag(item.id);
      if (kind === 'author')   await blogAdminAPI.deleteAuthor(item.id);
      fetchAll();
    } catch (err) {
      alert(err.response?.data?.message || 'Erro ao excluir');
    }
  };

  // -- Renderers ------------------------------------------------------------
  const renderPosts = () => (
    <AdminCard>
      <div className="admin-toolbar">
        <h3 style={{ margin: 0 }}>Posts ({posts.length})</h3>
        <button className="btn-primary" onClick={() => navigate('/admin/blog/new')}>
          <Plus size={16} /> Novo post
        </button>
      </div>
      <AdminTable
        columns={[
          { key: 'title', label: 'Título', render: (p) => (
            <div>
              <div style={{ fontWeight: 600 }}>{p.title}</div>
              <div style={{ fontSize: 12, opacity: 0.6 }}>/{p.slug}</div>
            </div>
          ) },
          { key: 'status', label: 'Status', render: (p) => {
            const s = STATUS_LABELS[p.status] || { label: p.status, color: '#999' };
            return <span style={{ color: s.color, fontWeight: 600 }}>{s.label}</span>;
          } },
          { key: 'category', label: 'Categoria', render: (p) => p.categories?.name || '—' },
          { key: 'author',   label: 'Autor',     render: (p) => p.authors?.name    || '—' },
          { key: 'program',  label: 'Programa',  render: (p) => p.programs?.label  || '—' },
          { key: 'published_at', label: 'Publicado em', render: (p) => p.published_at ? new Date(p.published_at).toLocaleDateString('pt-BR') : '—' },
          { key: 'actions', label: 'Ações', render: (p) => (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-icon" onClick={() => navigate(`/admin/blog/${p.id}/edit`)} title="Editar"><Edit2 size={16} /></button>
              <button className="btn-icon btn-danger" onClick={() => handleDeletePost(p)} title="Excluir"><Trash2 size={16} /></button>
            </div>
          ) },
        ]}
        data={posts}
      />
    </AdminCard>
  );

  const renderSimpleList = (kind, items, extraCols = []) => (
    <AdminCard>
      <div className="admin-toolbar">
        <h3 style={{ margin: 0 }}>{TABS.find((t) => t.id === `${kind}s`)?.label} ({items.length})</h3>
        <button className="btn-primary" onClick={() => openModal(kind)}>
          <Plus size={16} /> Novo
        </button>
      </div>
      <AdminTable
        columns={[
          { key: 'name', label: 'Nome' },
          { key: 'slug', label: 'Slug' },
          ...extraCols,
          { key: 'posts', label: 'Posts', render: (i) => i._count?.blog_posts ?? i._count?.blog_post_tags ?? 0 },
          { key: 'actions', label: 'Ações', render: (i) => (
            <div style={{ display: 'flex', gap: 8 }}>
              <button className="btn-icon" onClick={() => openModal(kind, i)} title="Editar"><Edit2 size={16} /></button>
              <button className="btn-icon btn-danger" onClick={() => handleDeleteGeneric(kind, i)} title="Excluir"><Trash2 size={16} /></button>
            </div>
          ) },
        ]}
        data={items}
      />
    </AdminCard>
  );

  const renderModal = () => {
    const { kind, item } = modal;
    if (!kind) return null;
    const titleMap = { category: 'Categoria', tag: 'Tag', author: 'Autor' };
    return (
      <AdminModal
        isOpen={modal.open}
        onClose={closeModal}
        title={`${item ? 'Editar' : 'Nova'} ${titleMap[kind]}`}
        footer={
          <>
            <button className="btn-secondary" onClick={closeModal}>Cancelar</button>
            <button className="btn-primary" onClick={handleModalSave}><Save size={16} /> Salvar</button>
          </>
        }
      >
        <form onSubmit={handleModalSave} className="admin-form">
          <div className="form-row">
            <label>Nome *</label>
            <input value={form.name || ''} onChange={(e) => setForm({ ...form, name: e.target.value })} required />
          </div>
          <div className="form-row">
            <label>Slug (opcional)</label>
            <input value={form.slug || ''} onChange={(e) => setForm({ ...form, slug: e.target.value })} placeholder="auto a partir do nome" />
          </div>
          {kind === 'category' && (
            <>
              <div className="form-row">
                <label>Descrição</label>
                <textarea rows={3} value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
              <div className="form-row">
                <label>Programa (opcional)</label>
                <select value={form.program_id || ''} onChange={(e) => setForm({ ...form, program_id: e.target.value })}>
                  <option value="">— Geral —</option>
                  {/* Programs are loaded by ProgramContext; usar slug do contexto não basta. */}
                </select>
              </div>
              <div className="form-row" style={{ display: 'flex', gap: 16 }}>
                <label><input type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Ativa</label>
                <label>Ordem: <input type="number" value={form.sort_order || 0} onChange={(e) => setForm({ ...form, sort_order: Number(e.target.value) })} style={{ width: 80 }} /></label>
              </div>
            </>
          )}
          {kind === 'author' && (
            <>
              <div className="form-row">
                <label>Bio</label>
                <textarea rows={3} value={form.bio || ''} onChange={(e) => setForm({ ...form, bio: e.target.value })} />
              </div>
              <div className="form-row">
                <label>Avatar URL</label>
                <input value={form.avatar_url || ''} onChange={(e) => setForm({ ...form, avatar_url: e.target.value })} />
              </div>
              <div className="form-row">
                <label>E-mail</label>
                <input type="email" value={form.email || ''} onChange={(e) => setForm({ ...form, email: e.target.value })} />
              </div>
              <div className="form-row" style={{ display: 'flex', gap: 12 }}>
                <input placeholder="Twitter" value={form.twitter || ''} onChange={(e) => setForm({ ...form, twitter: e.target.value })} />
                <input placeholder="LinkedIn" value={form.linkedin || ''} onChange={(e) => setForm({ ...form, linkedin: e.target.value })} />
              </div>
              <label><input type="checkbox" checked={form.is_active !== false} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} /> Ativo</label>
            </>
          )}
        </form>
      </AdminModal>
    );
  };

  return (
    <AdminLayout>
      <div className="admin-page">
        <header className="admin-page-header">
          <h1>Blog / CMS</h1>
          <p>Gerencie posts, categorias, tags e autores.</p>
        </header>

        <div className="admin-tabs" style={{ display: 'flex', gap: 8, marginBottom: 20 }}>
          {TABS.map((t) => (
            <button
              key={t.id}
              className={`admin-tab ${tab === t.id ? 'active' : ''}`}
              onClick={() => setTab(t.id)}
            >
              {t.icon} {t.label}
            </button>
          ))}
        </div>

        {loading ? <div>Carregando…</div> : (
          <>
            {tab === 'posts'      && renderPosts()}
            {tab === 'categories' && renderSimpleList('category', categories, [
              { key: 'program', label: 'Programa', render: (c) => c.programs?.label || '—' },
            ])}
            {tab === 'tags'       && renderSimpleList('tag', tags)}
            {tab === 'authors'    && renderSimpleList('author', authors)}
          </>
        )}

        {renderModal()}
      </div>
    </AdminLayout>
  );
};

export default BlogListPage;
